/** Filtros y ordenamiento in-memory para catálogo — funciones puras, sin dependencia de DB. */

import { productMatchesCategoryFilter } from './categoryTypes';
import type { CatalogItemTypeFilter } from './productTypes';
import type { CatalogFilter, CatalogProductRow, CatalogSort } from './catalogTypes';
import { effectiveCatalogUnitCost } from './comboMetrics';
import { normalizeSearchText } from '../../shared/format/searchText';

export type CatalogFilterParams = {
  organizationId: string;
  search?: string;
  status?: CatalogFilter;
  category?: string | 'all';
  itemType?: CatalogItemTypeFilter;
  pinnedProductId?: string | null;
  brandId?: string | null;
  supplierId?: string | null;
};

const tokenizeSearchInput = (value: string) =>
  normalizeSearchText(value).split(/\s+/).filter(Boolean);

export function applyCatalogFiltersInMemory(
  rows: CatalogProductRow[],
  params: CatalogFilterParams
): CatalogProductRow[] {
  const {
    organizationId,
    search = '',
    status = 'active',
    category = 'all',
    itemType = 'all',
    pinnedProductId = null,
    brandId = null,
    supplierId = null,
  } = params;

  const searchTokens = tokenizeSearchInput(search.trim().toLowerCase());
  const brandIdVal =
    brandId != null && String(brandId).trim() !== '' ? String(brandId).trim() : '';
  const supplierTrim =
    supplierId != null && String(supplierId).trim() !== '' ? String(supplierId).trim() : '';

  return rows
    .filter((row) => (row.organizationId ? row.organizationId === organizationId : false))
    .filter((row) => row.isMisc !== true)
    .filter((row) =>
      pinnedProductId ? row.id === pinnedProductId : true
    )
    .filter((row) => (status === 'all' ? true : status === 'active' ? row.isActive : !row.isActive))
    .filter((row) => {
      if (itemType === 'all') return true;
      if (itemType === 'combo') return row.isCombo === true;
      if (itemType === 'recipe') return row.hasRecipe === true;
      if (itemType === 'physical') {
        return row.itemType === 'physical' && row.isCombo !== true;
      }
      return row.itemType === itemType;
    })
    .filter((row) => productMatchesCategoryFilter(row.categoryId, category))
    .filter((row) => {
      if (!brandIdVal) return true;
      return row.brandId != null && row.brandId === brandIdVal;
    })
    .filter((row) => {
      if (!supplierTrim) return true;
      return row.preferredSupplierId === supplierTrim;
    })
    .filter((row) => {
      if (pinnedProductId) return true;
      if (searchTokens.length === 0) return true;
      const haystack = normalizeSearchText(
        `${row.name} ${row.sku ?? ''} ${row.barcode ?? ''} ${row.brandName ?? ''}`
      );
      return searchTokens.every((token) => haystack.includes(token));
    });
}

export function sortCatalogRows(rows: CatalogProductRow[], sortBy: CatalogSort): CatalogProductRow[] {
  const sorted = [...rows];
  const byName = (a: CatalogProductRow, b: CatalogProductRow) =>
    a.name.localeCompare(b.name, 'es', { sensitivity: 'base' });

  const marginPct = (r: CatalogProductRow): number | null => {
    const unitCost = effectiveCatalogUnitCost(r);
    if (unitCost == null || unitCost <= 0) return null;
    return ((r.price - unitCost) / unitCost) * 100;
  };

  const costValue = (r: CatalogProductRow): number | null => effectiveCatalogUnitCost(r);

  const stockValue = (r: CatalogProductRow): number | null => {
    if (r.isCombo && r.comboSellableQty != null) return r.comboSellableQty;
    return r.stockQuantity ?? null;
  };

  sorted.sort((a, b) => {
    if (a.isActive !== b.isActive) return a.isActive ? -1 : 1;

    switch (sortBy) {
      case 'name_asc': return byName(a, b);
      case 'name_desc': return byName(b, a);
      case 'price_desc': return b.price - a.price || byName(a, b);
      case 'price_asc': return a.price - b.price || byName(a, b);
      case 'stock_desc': {
        const aQ = stockValue(a), bQ = stockValue(b);
        if (aQ === null && bQ === null) return byName(a, b);
        if (aQ === null) return 1;
        if (bQ === null) return -1;
        return bQ - aQ || byName(a, b);
      }
      case 'stock_asc': {
        const aQ = stockValue(a), bQ = stockValue(b);
        if (aQ === null && bQ === null) return byName(a, b);
        if (aQ === null) return 1;
        if (bQ === null) return -1;
        return aQ - bQ || byName(a, b);
      }
      case 'cost_desc': {
        const aC = costValue(a), bC = costValue(b);
        if (aC === null && bC === null) return byName(a, b);
        if (aC === null) return 1;
        if (bC === null) return -1;
        return bC - aC || byName(a, b);
      }
      case 'cost_asc': {
        const aC = costValue(a), bC = costValue(b);
        if (aC === null && bC === null) return byName(a, b);
        if (aC === null) return 1;
        if (bC === null) return -1;
        return aC - bC || byName(a, b);
      }
      case 'margin_desc': {
        const aM = marginPct(a), bM = marginPct(b);
        if (aM === null && bM === null) return byName(a, b);
        if (aM === null) return 1;
        if (bM === null) return -1;
        return bM - aM || byName(a, b);
      }
      case 'margin_asc': {
        const aM = marginPct(a), bM = marginPct(b);
        if (aM === null && bM === null) return byName(a, b);
        if (aM === null) return 1;
        if (bM === null) return -1;
        return aM - bM || byName(a, b);
      }
      default: {
        const aDate = new Date(a.updatedAt ?? a.createdAt ?? '').getTime();
        const bDate = new Date(b.updatedAt ?? b.createdAt ?? '').getTime();
        return bDate - aDate || byName(a, b);
      }
    }
  });
  return sorted;
}
